import React, { useState } from "react";
import { TextField, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import Geocode from "react-geocode";
import Map from "../components/Map";

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  mapContainer: {
    marginTop: "10px",
  },
  autocompleteError: {
    width: "100%",
    backgroundColor: "red",
    color: "white",
    padding: "5px",
    textAlign: "center",
    fontSize: "small",
  },
}));

export default function LocationAutocomplete(props) {
  const classes = useStyles();
  const [center, setCenter] = useState(props.center);
  const [value, setValue] = useState(props.value);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [autocompleteError, setAutocompleteError] = useState("");
  const loaded = React.useRef(false);

  Geocode.setApiKey(process.env.REACT_APP_GOOGLE_MAPS_API);
  Geocode.setLanguage("en");

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API}&libraries=places`,
        document.querySelector("head"),
        "google-maps"
      );
    }

    loaded.current = true;
  }

  const fetch = React.useMemo(
    () =>
      throttle((request, callback) => {
        // request.componentRestrictions = { country: ["ca"] };
        request.componentRestrictions = {
          country: process.env.REACT_APP_COUNTRYCODES?.split(","),
        };
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 200),
    []
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
      //   autocompleteService.setComponentRestrictions({ country: country: process.env.REACT_APP_COUNTRYCODES.split(",") });
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  const onMarkerChanged = (center) => {
    Geocode.fromLatLng(center.lat, center.lng).then(
      (response) => {
        const location = response.results[0].formatted_address;
        props.onChange({ map: `${center.lat},${center.lng}`, location });
        setValue(location);
      },
      (error) => {
        console.error(error);
      }
    );
  };

  const onAutocompleteChange = (event, newValue) => {
    setOptions(newValue ? [newValue, ...options] : options);
    setValue(newValue);

    if (newValue?.description) {
      geocodeByAddress(newValue?.description)
        .then((results) => getLatLng(results[0]))

        .then((latLng) => {
          setCenter(latLng);
          props.onChange({
            map: `${latLng.lat},${latLng.lng}`,
            location: newValue?.description,
          });
          setAutocompleteError("");
        })
        .catch((error) => {
          setCenter(null);
          props.onChange({ map: "", location: newValue?.description });
          setAutocompleteError("No connection. Try again later.");
        });
    } else {
      setCenter(null);
      props.onChange({ map: "", location: newValue?.description });
      setAutocompleteError("");
    }
  };

  return (
    <div className="MuiFormControl-root MuiTextField-root MuiFormControl-marginNormal MuiFormControl-fullWidth">
      <Autocomplete
        id="google-map-demo"
        freeSolo
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.description
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        onBlur={() => {
          const filteredOptions = options?.filter(
            (option) =>
              option.description === inputValue || option === inputValue
          );

          if (filteredOptions?.length === 0) {
            setValue("");
            props.onChange({ map: "", location: "" });
          }
        }}
        includeInputInList
        filterSelectedOptions
        value={value}
        onChange={onAutocompleteChange}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add a location"
            variant="outlined"
            fullWidth
            error={!!props.error}
            helperText={props.error}
          />
        )}
        renderOption={(option) => {
          if (!option?.structured_formatting) {
            return null;
          }

          const matches =
            option.structured_formatting.main_text_matched_substrings;
          const parts = parse(
            option.structured_formatting.main_text,
            matches.map((match) => [match.offset, match.offset + match.length])
          );

          return (
            <Grid container alignItems="center">
              <Grid item>
                <LocationOnIcon className={classes.icon} />
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{ fontWeight: part.highlight ? 700 : 400 }}
                  >
                    {part.text}
                  </span>
                ))}

                <Typography variant="body2" color="textSecondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          );
        }}
      />

      <div className={classes.mapContainer}>
        {autocompleteError && (
          <div className={classes.autocompleteError}>{autocompleteError}</div>
        )}
        <Map
          isMarkerShown
          googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API}&v=3.exp&libraries=geometry,drawing,places`}
          center={center}
          loadingElement={<div style={{ height: `100%` }} />}
          containerElement={<div style={{ height: `400px` }} />}
          mapElement={<div style={{ height: `100%` }} />}
          onMarkerChanged={onMarkerChanged}
        />
      </div>
    </div>
  );
}
